// ===============================
// AXOIX i18n Configuration
// 81 Languages: 22 Indian + 59 Global
// ALL languages have FULL native translations
// ===============================
//
// PERFORMANCE — WHY ONLY ENGLISH IS IMPORTED STATICALLY
// -----------------------------------------------------
// These 81 locale files are ~22 MB of JSON. Importing them all statically (as
// this file used to) welded every one of them into main.js, producing a ~38 MB
// bundle. Until that finished downloading and executing, React had not mounted,
// so a visitor was interacting with the prerendered HTML snapshot: no
// JS-dependent UI (e.g. the demo buttons, which appear only after a live
// availability fetch), and every <Link> behaving as a plain <a> — a full page
// load that nginx's `try_files ... /index.html` answers with the prerendered
// HOMEPAGE. That is what "the button took me to the home page" actually was.
//
// English is bundled (it is the fallback, the prerender language, and what most
// visitors see first). The other 80 are code-split by webpack and fetched on
// demand when a visitor selects that language.
//
// TYPE-SAFETY NOTE: `resources` used to be typed `Record<string, { translation:
// typeof en }>`, which made the compiler reject any locale missing a key added
// to en.json. Lazy loading removes that compile-time check, so the equivalent
// guarantee now lives in `scripts/check-locales.js`, run as `prebuild`. If you
// add a key to en.json, that script — not tsc — is what fails the build.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { isRtlLanguage } from './languages';

// ===== ENGLISH (Base) — the only statically bundled locale =====
import en from './locales/en.json';

// Every locale code that has a file in ./locales. Guarded so an unrecognised
// detected language (e.g. "en-GB") falls back to English instead of triggering
// a chunk request that 404s.
const LOCALE_CODES = [
  // Indian (22)
  'hi', 'bn', 'te', 'mr', 'ta', 'gu', 'kn', 'ml', 'or', 'pa', 'as', 'mai',
  'sa', 'ur', 'sd', 'ne', 'kok', 'doi', 'ks', 'mni', 'brx', 'sat',
  // European (30)
  'es', 'fr', 'de', 'it', 'pt', 'ru', 'nl', 'pl', 'sv', 'no', 'da', 'fi',
  'el', 'cs', 'ro', 'hu', 'uk', 'bg', 'hr', 'sk', 'sl', 'sr', 'lt', 'lv',
  'et', 'ca', 'eu', 'gl', 'is', 'ga', 'cy',
  // Asian (13)
  'ja', 'ko', 'zh', 'zh-TW', 'th', 'vi', 'id', 'ms', 'fil', 'my', 'km', 'tr',
  // Middle Eastern (4)
  'ar', 'he', 'fa', 'ps',
  // African (5)
  'af', 'sw', 'zu', 'xh', 'am',
  // Central Asian (6)
  'uz', 'kk', 'az', 'ka', 'hy', 'mn',
];

const SUPPORTED = new Set(LOCALE_CODES);

// Locale codes that map onto another file rather than one of their own.
const ALIASES: Record<string, string> = {
  'zh-CN': 'zh',
};

/**
 * i18next backend that resolves a language to its code-split JSON chunk.
 * The template literal keeps webpack in control: it emits one chunk per file in
 * ./locales, fetched only when that language is actually requested.
 */
const lazyLocaleBackend = {
  type: 'backend' as const,
  init: () => {},
  read(
    language: string,
    _namespace: string,
    callback: (err: unknown, data?: unknown) => void
  ) {
    const file = ALIASES[language] || language;

    // English is already in memory — never round-trip for it.
    if (file === 'en') {
      callback(null, en);
      return;
    }

    if (!SUPPORTED.has(file)) {
      // Unknown code: hand back nothing and let fallbackLng resolve to English.
      callback(null, {});
      return;
    }

    import(
      /* webpackChunkName: "locale-[request]" */
      `./locales/${file}.json`
    )
      .then((mod) => callback(null, (mod as { default?: unknown }).default || mod))
      .catch((err) => callback(err));
  },
};

// Re-export from languages.ts
export { allLanguages as supportedLanguages, getLanguageByCode as getLanguageInfo } from './languages';

// Initialize i18n
i18n
  .use(lazyLocaleBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // English ships with the bundle so the first paint never waits on a fetch.
    resources: { en: { translation: en } },
    // Required when `resources` is present AND a backend supplies the rest —
    // without it i18next assumes the bundled resources are exhaustive and
    // never calls the backend.
    partialBundledLanguages: true,
    fallbackLng: 'en',
    debug: process.env.NODE_ENV === 'development',
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      order: ['querystring', 'cookie', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage', 'cookie'],
    },
    react: {
      // Render English immediately and re-render when the locale chunk lands,
      // rather than suspending the tree while it downloads.
      useSuspense: false,
    },
  });

// Handle RTL languages - update document direction on language change
i18n.on('languageChanged', (lng) => {
  const isRtl = isRtlLanguage(lng);
  document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
  document.documentElement.lang = lng;
});

export default i18n;
